import { useState, useEffect, useRef } from "react";
import { CustomerMergeSearchResult } from "@Shape-Digital/kudzu-data/lib/types/actions";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

const useSearchCustomersForMerge = (query: string) => {
  const [results, setResults] = useState<CustomerMergeSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchCustomersForMerge = useFirebaseAppFunction(
    "searchCustomersForMerge",
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Every state change is tagged with the request it belongs to. Searches
  // overlap (the debounce only delays a request, it can't cancel one already
  // in flight), and responses can come back out of order — without this
  // guard a stale response overwrites the newest results, or flips the
  // loading flag off/on around a request that is still running, leaving the
  // dropdown stuck on "Loading…" with the wrong list underneath.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    // Bump first: anything already in flight belongs to a previous query and
    // must no longer touch state.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsLoading(false);
      return undefined;
    }

    // Enter the loading state as soon as the query is searchable, not when
    // the debounced request starts — otherwise the dropdown shows "No
    // customers found" for the debounce window before the search even runs.
    setIsLoading(true);

    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await searchCustomersForMerge({ query });
        if (requestIdRef.current !== requestId) return;
        setResults(data ?? []);
      } catch {
        if (requestIdRef.current !== requestId) return;
        setResults([]);
      } finally {
        if (requestIdRef.current === requestId) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, searchCustomersForMerge]);

  return { results, isLoading };
};

export default useSearchCustomersForMerge;
